import type { DesignScopeCoordinator } from "./designScopeCoordinator";
import type { DbExecutor } from "./executor";
import type { FeatureRepository } from "./repositories/featureRepository";
import type { AssemblyRepository } from "./repositories/assemblyRepository";
import type { PlanRepository } from "./repositories/planRepository";

/**
 * REPLACING A DOOR IS NOT CLOSING A HOLE (spec §85).
 *
 * A door swap reuses the opening that is already there. It buys a door,
 * hardware and the labour to hang it, plus the small cost of taking the
 * old one off — and it buys NONE of the framing, sheathing and patching
 * that closing an opening costs, nor the framing a brand-new opening
 * costs. Deleting the door and drawing another one says something quite
 * different to the change analysis (§9): that one opening was closed and
 * another was cut. Saying "replace this door" says what actually happens.
 *
 * Only the DECISION is stored. The feature, its quantity and its money
 * are derived here from the decisions that still point at a live opening,
 * so a door that is later deleted takes its replacement scope with it.
 */

export type DoorReplacementKind = "interior" | "exterior";

const DEFINITION: Record<DoorReplacementKind, string> = {
  interior: "replace-interior-door",
  exterior: "replace-exterior-door",
};

export interface OpeningReplacement {
  rootOpeningId: string;
  doorKind: DoorReplacementKind;
}

export class OpeningReplacementService {
  private scope: DesignScopeCoordinator | null = null;
  setScopeCoordinator(scope: DesignScopeCoordinator): void { this.scope = scope; }

  constructor(
    private readonly db: DbExecutor,
    private readonly features: FeatureRepository,
    private readonly assemblies: AssemblyRepository,
    private readonly plans: PlanRepository,
  ) {}

  /** Every replacement decision this design holds, by opening ROOT id. */
  async listForDesign(designId: string): Promise<OpeningReplacement[]> {
    const rows = await this.db.select<{ root_opening_id: string; door_kind: string }>(
      "SELECT root_opening_id, door_kind FROM opening_replacements WHERE design_id = ?",
      [designId],
    );
    return rows.map((r) => ({ rootOpeningId: r.root_opening_id, doorKind: r.door_kind as DoorReplacementKind }));
  }

  /**
   * Record (or clear, with null) the decision for one opening, then bring
   * the scope back in line. Existing documents what is there and never
   * carries scope, so a replacement on it is refused rather than silently
   * ignored.
   */
  async setReplacement(designId: string, openingId: string, kind: DoorReplacementKind | null): Promise<void> {
    const design = await this.db.select<{ kind: string; project_id: string }>(
      "SELECT kind, project_id FROM designs WHERE id = ?",
      [designId],
    );
    if (!design[0]) throw new Error("That design no longer exists.");
    if (design[0].kind === "existing") {
      throw new Error("Existing records the house as it is. Replace a door in a proposed plan.");
    }
    const root = await this.rootOf(openingId);
    if (root === null) throw new Error("That door no longer exists.");
    await this.db.transaction(async () => {
      if (kind === null) {
        await this.db.execute(
          "DELETE FROM opening_replacements WHERE design_id = ? AND root_opening_id = ?",
          [designId, root],
        );
      } else {
        await this.db.execute(
          `INSERT INTO opening_replacements (design_id, root_opening_id, door_kind, created_at)
             VALUES (?, ?, ?, ?)
           ON CONFLICT (design_id, root_opening_id) DO UPDATE SET door_kind = excluded.door_kind`,
          [designId, root, kind, new Date().toISOString()],
        );
      }
      await this.reconcile(designId);
    });
  }

  /**
   * Derive the scope: ONE feature per (design, door kind), its quantity the
   * number of doors actually being replaced. A decision whose opening has
   * been deleted stops counting — and when the last one goes, the feature
   * archives itself unless somebody has since touched it or spent money
   * against it (the Phase 8 reconciliation rules, unchanged).
   */
  async reconcile(designId: string): Promise<string[]> {
    const design = await this.db.select<{ project_id: string; kind: string }>(
      "SELECT project_id, kind FROM designs WHERE id = ?",
      [designId],
    );
    if (!design[0] || design[0].kind === "existing") return [];
    const projectId = design[0].project_id;

    /* Only decisions whose door is still drawn, and still a door. */
    const live = await this.db.select<{ door_kind: string; n: number }>(
      `SELECT r.door_kind, COUNT(*) AS n
         FROM opening_replacements r
         JOIN wall_openings o ON COALESCE(o.origin_id, o.id) = r.root_opening_id
         JOIN walls w ON w.id = o.wall_id
        WHERE r.design_id = ? AND w.design_id = ? AND o.archived_at IS NULL
          AND w.archived_at IS NULL AND o.kind = 'door'
        GROUP BY r.door_kind`,
      [designId, designId],
    );
    const counts = new Map<string, number>(live.map((r) => [r.door_kind, r.n]));

    const touched: string[] = [];
    for (const kind of ["interior", "exterior"] as const) {
      const definitionId = DEFINITION[kind];
      const count = counts.get(kind) ?? 0;
      const featureId = await this.featureFor(designId, projectId, definitionId, count > 0);
      if (featureId === null) continue;
      const quantityX100 = count * 100;
      const current = await this.features.getById(featureId);
      if (count === 0) {
        if (current && current.archivedAt === null) {
          if (this.scope && !await this.scope.isAutoRemovable(featureId)) {
            if (current.quantityGeometryX100 !== null) {
              await this.features.setGeometryQuantity(featureId, null);
              await this.assemblies.regenerateForFeature(featureId);
              touched.push(featureId);
            }
            continue;
          }
          await this.db.execute(
            "UPDATE project_features SET archived_at = ?, updated_at = ?, quantity_geometry_x100 = NULL WHERE id = ?",
            [new Date().toISOString(), new Date().toISOString(), featureId],
          );
          await this.scope?.archiveGeneratedLines(featureId, new Date().toISOString());
          touched.push(featureId);
        }
        continue;
      }
      if (!current || current.quantityGeometryX100 !== quantityX100) {
        await this.features.setGeometryQuantity(featureId, quantityX100);
        touched.push(featureId);
      }
      await this.autoAttach(featureId, definitionId);
      try {
        await this.assemblies.regenerateForFeature(featureId);
      } catch {
        /* surfaced in the assembly panel, never fatal to drawing */
      }
    }
    return touched;
  }

  /** The scope this design's door replacements landed on, if any. */
  async featureIdFor(designId: string, kind: DoorReplacementKind): Promise<string | null> {
    const rows = await this.db.select<{ id: string }>(
      `SELECT id FROM project_features
        WHERE design_id = ? AND feature_definition_id = ? AND quantity_geometry_binding = 'opening-replacement-count'
          AND archived_at IS NULL LIMIT 1`,
      [designId, DEFINITION[kind]],
    );
    return rows[0]?.id ?? null;
  }

  /** The opening's root identity — what survives duplicating a design. */
  private async rootOf(openingId: string): Promise<string | null> {
    const rows = await this.db.select<{ root: string; kind: string }>(
      "SELECT COALESCE(origin_id, id) AS root, kind FROM wall_openings WHERE id = ? AND archived_at IS NULL",
      [openingId],
    );
    if (!rows[0]) return null;
    if (rows[0].kind !== "door") throw new Error("Only a door can be replaced this way.");
    return rows[0].root;
  }

  /** One feature per (design, definition); revived rather than duplicated. */
  private async featureFor(
    designId: string,
    projectId: string,
    definitionId: string,
    create: boolean,
  ): Promise<string | null> {
    const existing = await this.db.select<{ id: string; archived_at: string | null }>(
      `SELECT id, archived_at FROM project_features
        WHERE design_id = ? AND feature_definition_id = ? AND quantity_geometry_binding = 'opening-replacement-count'
        ORDER BY archived_at IS NULL DESC, created_at LIMIT 1`,
      [designId, definitionId],
    );
    if (existing[0]) {
      if (existing[0].archived_at !== null && create) {
        await this.db.execute("UPDATE project_features SET archived_at = NULL, updated_at = ? WHERE id = ?", [
          new Date().toISOString(),
          existing[0].id,
        ]);
      }
      return existing[0].id;
    }
    if (!create) return null;
    const fd = await this.db.select<{ display_name: string }>(
      "SELECT display_name FROM feature_definitions WHERE id = ?",
      [definitionId],
    );
    const feature = await this.features.create(projectId, {
      designId,
      roomId: null, // a door belongs to two rooms and to neither (§85)
      floorId: null,
      featureDefinitionId: definitionId,
      name: fd[0]?.display_name ?? definitionId,
      action: "replace",
      quantityX100: null,
      quantityUnit: "each",
      notes: "",
    });
    await this.features.setGeometryBinding(feature.id, "opening-replacement-count", null);
    return feature.id;
  }

  /** Phase 8.1: exactly one unit-compatible suggested template, or none. */
  private async autoAttach(featureId: string, definitionId: string): Promise<void> {
    if (this.scope) { await this.scope.ensureDefaultAssembly(featureId); return; }
    if ((await this.assemblies.listByFeature(featureId)).length > 0) return;
    const compatible = (await this.assemblies.listSuggestedTemplates(definitionId)).filter(
      (t) => t.drivingUnit === "each",
    );
    if (compatible.length !== 1) return;
    try {
      await this.assemblies.attachTemplate(featureId, compatible[0].id);
    } catch (err) {
      console.error("Door replacement auto-attach failed:", err);
    }
  }

  /** Whether the wall hosting this opening reads as exterior — the default
   *  the inspector offers, never a decision made on the user's behalf. */
  async suggestKindFor(designId: string, floorId: string, openingId: string): Promise<DoorReplacementKind> {
    const model = await this.plans.loadFloor(designId, floorId);
    const opening = model.openings.get(openingId);
    const wall = opening ? model.walls.get(opening.wallId) : null;
    if (wall?.exteriorOverride === true) return "exterior";
    if (wall?.exteriorOverride === false) return "interior";
    const { analyzePlan } = await import("../geometry");
    const analysis = analyzePlan(model);
    return wall && analysis.exteriorWallIds?.has(wall.id) ? "exterior" : "interior";
  }
}
